import type { Permission } from '../../types/auth';
import {
    Work as WorkIcon,
    Restaurant as RestaurantIcon,
    GolfCourse as GolfCourseIcon,
    AttachMoney as MoneyIcon,
    ShoppingCart as ShoppingCartIcon,
    Event as EventIcon,
    Payments as PaymentsIcon,
    Assignment as AssignmentIcon,
    MonetizationOn as RevenueIcon,
    People as PeopleIcon,
    Settings as SettingsIcon,
    Build as BuildIcon,
    AccessTime as PunchClockIcon,
    Inventory as InventoryIcon,
    Construction as ConstructionIcon,
    Store as StoreIcon,
    Security as SecurityIcon,
    History as HistoryIcon,
    AllInbox as LockerIcon,
    Category as CategoryIcon,
} from '@/shared/ui/icons';

export interface NavItem {
    textKey: string;
    icon: React.ReactNode;
    path: string;
    permission?: Permission;
}

export interface MenuSection {
    titleKey: string;
    items: NavItem[];
}

export const menuSections: MenuSection[] = [
    {
        titleKey: 'nav.sections.courseManagement',
        items: [
            {
                textKey: 'nav.items.courseReservation',
                icon: <EventIcon />,
                path: '/reservations',
                permission: 'reservations:read',
            },
            {
                textKey: 'nav.items.todayPlay',
                icon: <GolfCourseIcon />,
                path: '/play/today',
                permission: 'playRecords:read',
            },
            {
                textKey: 'nav.items.courseMaintenance',
                icon: <WorkIcon />,
                path: '/maintenance',
                permission: 'maintenance:read',
            },
            {
                textKey: 'nav.items.holeInfo',
                icon: <GolfCourseIcon />,
                path: '/holes',
                permission: 'holes:read',
            },
        ],
    },
    {
        titleKey: 'nav.sections.memberManagement',
        items: [
            {
                textKey: 'nav.items.memberData',
                icon: <PeopleIcon />,
                path: '/members',
                permission: 'members:read',
            },
            {
                textKey: 'nav.items.memberConsumption',
                icon: <ShoppingCartIcon />,
                path: '/members/consumption',
                permission: 'members:read',
            },
            {
                textKey: 'nav.items.checkout',
                icon: <PaymentsIcon />,
                path: '/checkout',
                permission: 'checkout:process',
            },
        ],
    },
    {
        titleKey: 'nav.sections.employeeManagement',
        items: [
            {
                textKey: 'nav.items.employeeData',
                icon: <WorkIcon />,
                path: '/employees',
                permission: 'employees:read',
            },
            {
                textKey: 'nav.items.attendance',
                icon: <EventIcon />,
                path: '/attendance',
                permission: 'attendance:read',
            },
            {
                textKey: 'nav.items.schedule',
                icon: <AssignmentIcon />,
                path: '/schedule',
                permission: 'schedule:read',
            },
            {
                textKey: 'nav.items.salary',
                icon: <MoneyIcon />,
                path: '/salary',
                permission: 'salary:read',
            },
        ],
    },
    {
        titleKey: 'nav.sections.restaurantManagement',
        items: [
            {
                textKey: 'nav.items.restaurantManagement',
                icon: <RestaurantIcon />,
                path: '/restaurants',
                permission: 'menu:read',
            },
            {
                textKey: 'nav.items.menuManagement',
                icon: <RestaurantIcon />,
                path: '/menu',
                permission: 'menu:read',
            },
            {
                textKey: 'nav.items.orderManagement',
                icon: <ShoppingCartIcon />,
                path: '/orders',
                permission: 'orders:read',
            },
        ],
    },
    {
        titleKey: 'nav.sections.shopManagement',
        items: [
            {
                textKey: 'nav.items.shopPos',
                icon: <StoreIcon />,
                path: '/shop',
                permission: 'consumption:write',
            },
            {
                textKey: 'nav.items.shopManagement',
                icon: <StoreIcon />,
                path: '/shop/management',
                permission: 'shop:write',
            },
            {
                textKey: 'nav.items.productManagement',
                icon: <InventoryIcon />,
                path: '/shop/products',
                permission: 'shop:write',
            },
            {
                textKey: 'nav.items.categoryManagement',
                icon: <CategoryIcon />,
                path: '/shop/categories',
                permission: 'shop:write',
            },
        ],
    },
    {
        titleKey: 'nav.sections.teegle',
        items: [
            {
                textKey: 'nav.items.teeSheetManagement',
                icon: <GolfCourseIcon />,
                path: '/teesheet',
                permission: 'teesheet:read',
            },
        ],
    },
    {
        titleKey: 'nav.sections.financeManagement',
        items: [
            {
                textKey: 'nav.items.revenueManagement',
                icon: <RevenueIcon />,
                path: '/revenue',
                permission: 'revenue:read',
            },
        ],
    },
    {
        titleKey: 'nav.sections.inventoryAsset',
        items: [
            {
                textKey: 'nav.items.shopInventory',
                icon: <InventoryIcon />,
                path: '/inventory/shop',
                permission: 'shop:read',
            },
            {
                textKey: 'nav.items.consumableManagement',
                icon: <InventoryIcon />,
                path: '/consumables',
                permission: 'consumable:read',
            },
            {
                textKey: 'nav.items.assetMaintenance',
                icon: <ConstructionIcon />,
                path: '/asset-maintenance',
                permission: 'asset:read',
            },
        ],
    },
    {
        titleKey: 'nav.sections.personal',
        items: [
            { textKey: 'nav.items.punchClock', icon: <PunchClockIcon />, path: '/punch-clock' },
        ],
    },
    {
        titleKey: 'nav.sections.systemSettings',
        items: [
            {
                textKey: 'nav.items.courseSettings',
                icon: <SettingsIcon />,
                path: '/settings/course',
            },
            {
                textKey: 'nav.items.subCourseManagement',
                icon: <GolfCourseIcon />,
                path: '/settings/courses',
            },
            {
                textKey: 'nav.items.reservationSlotSettings',
                icon: <EventIcon />,
                path: '/settings/reservation-slots',
            },
            {
                textKey: 'nav.items.facilityStatus',
                icon: <BuildIcon />,
                path: '/settings/facilities',
                permission: 'facility:read',
            },
            {
                textKey: 'nav.items.lockerManagement',
                icon: <LockerIcon />,
                path: '/settings/lockers',
                permission: 'locker:read',
            },
            {
                textKey: 'nav.items.rolePermission',
                icon: <SecurityIcon />,
                path: '/settings/roles',
                permission: 'employees:write',
            },
            {
                textKey: 'nav.items.auditLog',
                icon: <HistoryIcon />,
                path: '/settings/audit-logs',
                permission: 'auditLogs:read',
            },
        ],
    },
];

// /dev-tools 路由只在開發建置存在（App.tsx），選單用同一個建置期常數判斷，不靠網域猜測
export const devMenuSections: MenuSection[] = import.meta.env.DEV
    ? [
          {
              titleKey: 'nav.sections.devTools',
              items: [{ textKey: 'nav.items.devTools', icon: <BuildIcon />, path: '/dev-tools' }],
          },
      ]
    : [];

/** 由側邊欄選單設定反查頁面名稱的翻譯鍵，讓麵包屑與選單用同一份名稱 */
export function findNavItemTextKey(path: string): string | undefined {
    for (const section of [...menuSections, ...devMenuSections]) {
        const matched = section.items.find((item) => item.path === path);
        if (matched) {
            return matched.textKey;
        }
    }
    return undefined;
}
